import { Add as AddIcon, CameraAltOutlined as CameraIcon } from '@mui/icons-material';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useInstagramStatus, useInstagramOAuthFlow } from '@/hooks/queries/instagram';
import { useAutomations, useStatsOverview } from '@/hooks/queries/dm-automation';
import type { TriggerType } from '@/types/dmAutomation';
import AutomationCard from './AutomationCard';
import { ACCENT, ACCENT_HOVER, ACCENT_TINT, ACCENT_TINT_STRONG, PageHeader, TRIGGER_LABEL } from './_shared';

interface Props {
    onStartWizard: () => void;
    onGoToAccount: () => void;
    onEdit: (automation: import('@/types/dmAutomation').DmAutomationDto) => void;
    selectedId: string | null;
    onSelect: (id: string | null) => void;
}

const StatCard = ({ label, value, loading }: { label: string; value: string | number; loading?: boolean }) => (
    <Card variant="outlined" sx={{ borderRadius: 3, flex: 1 }}>
        <CardContent>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {label}
            </Typography>
            {loading ? (
                <Skeleton width={60} height={36} sx={{ mt: 0.5 }} />
            ) : (
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
                    {value}
                </Typography>
            )}
        </CardContent>
    </Card>
);

type Filter = 'all' | TriggerType;

const FILTERS: { key: Filter; label: string }[] = [
    { key: 'all', label: '전체' },
    { key: 'COMMENT_KEYWORD', label: TRIGGER_LABEL.COMMENT_KEYWORD },
    { key: 'STORY_REPLY', label: TRIGGER_LABEL.STORY_REPLY },
    { key: 'DM_KEYWORD', label: TRIGGER_LABEL.DM_KEYWORD },
];

const AutomationListPage = ({ onStartWizard, onGoToAccount, onEdit, selectedId, onSelect }: Props) => {
    const statusQuery = useInstagramStatus({ withProfile: true });
    const overviewQuery = useStatsOverview({ enabled: statusQuery.data?.isConnected === true });
    const automationsQuery = useAutomations({ enabled: statusQuery.data?.isConnected === true });
    const oauth = useInstagramOAuthFlow();

    const [filter, setFilter] = useState<Filter>('all');

    const isConnected = statusQuery.data?.isConnected === true;
    const overview = overviewQuery.data;
    const automations = automationsQuery.data ?? [];
    const filtered = useMemo(
        () => (filter === 'all' ? automations : automations.filter((a) => a.triggerType === filter)),
        [automations, filter],
    );

    const renderAccountStrip = () => {
        if (statusQuery.isLoading) {
            return (
                <Card variant="outlined" sx={{ borderRadius: 3, mb: 2.5 }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Skeleton width={140} height={20} />
                            <Skeleton width={200} height={16} />
                        </Box>
                    </CardContent>
                </Card>
            );
        }

        if (!isConnected) {
            return (
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 3,
                        mb: 2.5,
                        background: 'linear-gradient(135deg,#fff8f2 0%,#ffe9d9 100%)',
                        borderColor: '#ffd2b8',
                    }}
                >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2.5,
                                bgcolor: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <CameraIcon sx={{ color: ACCENT, fontSize: 26 }} />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                먼저 Instagram 계정을 연결하세요
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                연결 후 자동화를 만들 수 있어요
                            </Typography>
                        </Box>
                        <Button
                            onClick={oauth.start}
                            disabled={oauth.isBusy}
                            variant="contained"
                            disableElevation
                            startIcon={oauth.isBusy ? <CircularProgress size={14} color="inherit" /> : undefined}
                            sx={{
                                bgcolor: ACCENT,
                                color: '#fff',
                                fontWeight: 700,
                                textTransform: 'none',
                                borderRadius: 2,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: ACCENT_HOVER },
                                '&.Mui-disabled': { bgcolor: ACCENT, opacity: 0.6, color: '#fff' },
                            }}
                        >
                            {oauth.isPolling ? '연결 확인 중…' : 'Instagram 연결하기'}
                        </Button>
                    </CardContent>
                </Card>
            );
        }

        const data = statusQuery.data!;
        return (
            <Card variant="outlined" sx={{ borderRadius: 3, mb: 2.5 }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                    <Avatar
                        src={data.profilePictureUrl || undefined}
                        sx={{ width: 40, height: 40, bgcolor: ACCENT_TINT_STRONG }}
                    >
                        <CameraIcon sx={{ color: ACCENT, fontSize: 20 }} />
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                @{data.instagramUsername}
                            </Typography>
                            <Chip
                                size="small"
                                label="연결됨"
                                sx={{
                                    height: 20,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: 'rgba(76,175,80,0.12)',
                                    color: '#2e7d32',
                                }}
                            />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            {typeof data.mediaCount === 'number' && `게시물 ${data.mediaCount.toLocaleString()}`}
                            {typeof data.followersCount === 'number' &&
                                ` · 팔로워 ${data.followersCount.toLocaleString()}`}
                            {typeof data.followsCount === 'number' && ` · 팔로잉 ${data.followsCount.toLocaleString()}`}
                        </Typography>
                    </Box>
                    <Button
                        onClick={onGoToAccount}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'none', borderRadius: 2, cursor: 'pointer' }}
                    >
                        관리
                    </Button>
                </CardContent>
            </Card>
        );
    };

    return (
        <Box>
            <PageHeader
                title="자동화 관리"
                desc="자동으로 답장이 나가는 규칙들을 관리합니다"
                action={
                    <Button
                        onClick={onStartWizard}
                        disabled={!isConnected}
                        variant="contained"
                        startIcon={<AddIcon />}
                        disableElevation
                        sx={{
                            bgcolor: ACCENT,
                            color: '#fff',
                            fontWeight: 700,
                            textTransform: 'none',
                            borderRadius: 2,
                            cursor: 'pointer',
                            transition: 'transform 120ms ease, background-color 120ms ease',
                            willChange: 'transform',
                            '&:hover': { bgcolor: ACCENT_HOVER, transform: 'translateY(-1px)' },
                            '&:active': { transform: 'translateY(0)' },
                            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                        }}
                    >
                        새 자동화 만들기
                    </Button>
                }
            />

            {renderAccountStrip()}

            {isConnected && overviewQuery.isError && (
                <Alert severity="warning" sx={{ borderRadius: 2, mb: 2.5 }}>
                    통계를 불러오지 못했어요. 잠시 후 다시 시도해주세요.
                </Alert>
            )}

            <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
                <StatCard
                    label="총 발송"
                    value={overview ? overview.totalSent.toLocaleString() : '—'}
                    loading={isConnected && overviewQuery.isLoading}
                />
                <StatCard
                    label="오픈율"
                    value={overview ? `${overview.openRate}%` : '—'}
                    loading={isConnected && overviewQuery.isLoading}
                />
                <StatCard
                    label="클릭율"
                    value={overview ? `${overview.clickRate}%` : '—'}
                    loading={isConnected && overviewQuery.isLoading}
                />
                <StatCard
                    label="리드"
                    value={overview ? overview.totalLeads.toLocaleString() : '—'}
                    loading={isConnected && overviewQuery.isLoading}
                />
            </Stack>

            {isConnected && (
                <>
                    <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                            bgcolor: 'action.hover',
                            p: 0.5,
                            borderRadius: 2,
                            mb: 1.5,
                            width: 'fit-content',
                            maxWidth: '100%',
                            overflowX: 'auto',
                        }}
                    >
                        {FILTERS.map((f) => {
                            const active = filter === f.key;
                            return (
                                <Button
                                    key={f.key}
                                    onClick={() => setFilter(f.key)}
                                    disableRipple
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: 12,
                                        px: 1.5,
                                        py: 0.5,
                                        minWidth: 'auto',
                                        borderRadius: 1.5,
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                        color: active ? 'text.primary' : 'text.secondary',
                                        bgcolor: active ? 'background.paper' : 'transparent',
                                        boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: active ? 'background.paper' : 'transparent' },
                                    }}
                                >
                                    {f.label}
                                </Button>
                            );
                        })}
                    </Stack>

                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                        내 자동화 {automations.length}개
                        {filter !== 'all' && ` · ${filtered.length}개 표시 중`}
                    </Typography>

                    {automationsQuery.isLoading ? (
                        <Stack spacing={1}>
                            {[0, 1, 2].map((i) => (
                                <Skeleton key={i} variant="rounded" height={84} sx={{ borderRadius: 3 }} />
                            ))}
                        </Stack>
                    ) : automationsQuery.isError ? (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                            자동화 목록을 불러오지 못했어요.
                        </Alert>
                    ) : filtered.length === 0 ? (
                        <Card
                            variant="outlined"
                            sx={{
                                borderRadius: 3,
                                borderStyle: 'dashed',
                                bgcolor: 'transparent',
                            }}
                        >
                            <CardContent sx={{ py: 6, textAlign: 'center' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {filter === 'all' ? '아직 자동화가 없어요' : '이 종류의 자동화가 없어요'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    새 자동화를 만들면 여기에 표시됩니다
                                </Typography>
                                {filter === 'all' && (
                                    <Button
                                        onClick={onStartWizard}
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        disableElevation
                                        sx={{
                                            bgcolor: ACCENT,
                                            color: '#fff',
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: ACCENT_HOVER },
                                        }}
                                    >
                                        새 자동화 만들기
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    ) : (
                        <Stack spacing={1}>
                            {filtered.map((a) => (
                                <AutomationCard
                                    key={a.id}
                                    automation={a}
                                    isSelected={a.id === selectedId}
                                    onSelect={() => onSelect(a.id === selectedId ? null : a.id)}
                                    onEdit={onEdit}
                                />
                            ))}
                        </Stack>
                    )}
                </>
            )}
        </Box>
    );
};

export default AutomationListPage;
